import { graphql } from '~publish/graphql'

import { useDismissAllCommentsForChannels } from './useDismissAllCommentsForChannels'
import { useDismissAllCommentsForPost } from './useDismissAllCommentsForPost'

export const DismissAllCommentsForChannelsMutation = graphql(/* GraphQL */ `
  mutation DismissAllCommentsForChannels(
    $input: DismissAllCommentsForChannelsInput!
  ) {
    dismissAllCommentsForChannels(input: $input) {
      __typename
      ... on DismissAllCommentsForChannelsSuccess {
        channels {
          id
          status
        }
      }
      ... on InvalidInputError {
        message
      }
      ... on NotFoundError {
        message
      }
      ... on UnauthorizedError {
        message
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`)

export const DismissAllCommentsByPostIdMutation = graphql(/* GraphQL */ `
  mutation DismissAllCommentsByPostId(
    $input: DismissAllCommentsByPostIdInput!
  ) {
    dismissAllCommentsByPostId(input: $input) {
      __typename
      ... on DismissAllCommentsSuccess {
        affectedCommentCount
        status
      }
      ... on InvalidInputError {
        message
      }
      ... on NotFoundError {
        message
      }
      ... on UnauthorizedError {
        message
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`)

export type DismissAllCommentsForChannels = (
  channelIds: string[],
  toDate?: string,
) => Promise<void>

export type DismissAllCommentsForPost = (postId: string) => Promise<void>

export type UseDismissAllResult = {
  dismissAllForChannels: DismissAllCommentsForChannels
  dismissAllForPost: DismissAllCommentsForPost
}

/**
 *
 * Hook to dismiss all comments in an organization, channel or post
 * @returns {UseDismissAllResult} - The dismissAllForChannels function
 *
 * @example
 * const { dismissAllForChannels } = useDismissAll()
 *
 * const handleDismissAllComments = useCallback(async (): Promise<void> => {
 *  await dismissAllForChannels([channelIds])
 * }, [dismissAllForChannels, channelIds])
 *
 * // With date filter
 * const handleDismissOlderComments = useCallback(async (): Promise<void> => {
 *  const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000
 *  const oneMonthAgo = new Date(Date.now() - ONE_MONTH_MS).toISOString()
 *  await dismissAllForChannels([channelIds], oneMonthAgo)
 * }, [dismissAllForChannels, channelIds])
 *
 */
export function useDismissAll(): UseDismissAllResult {
  const dismissAllForChannels = useDismissAllCommentsForChannels()
  const dismissAllForPost = useDismissAllCommentsForPost()

  return { dismissAllForChannels, dismissAllForPost }
}
